<script setup>
import {onBeforeMount, onUnmounted, reactive} from 'vue'
import {State, clone, waitRender} from '@/paks/vu-app'

const props = defineProps({modelValue: Object})

const emit = defineEmits(['update:modelValue'])

const ColorFields = [
{
        name: 'drag',
        title: 'Order',
        icon: (v, rec) => (rec.path != '/' ? '$drag' : ''),
        cursor: (v, rec) => (rec.path != '/' ? 'grab' : 'default'),
        width: '5%',
    },
    {name: 'delete', icon: '$close', width: '2%'},
    {name: 'name', editable: true, width: '30%'},
    {name: 'value', editable: true},
    {name: 'swatch', width: '10%', style: (v, rec) => `background-color: ${rec.value}`},
]

const Fields = [
    {name: 'delete', icon: '$close', width: '2%'},
    {name: 'name', editable: true, width: '30%'},
    {name: 'value', editable: true},
]

const page = reactive({
    bright: 'Dark',
    colors: null,
    display: null,
    drag: null,
    formats: null,
    lastKeyPress: null,
    mode: {},
    showColors: {},
    theme: null,
    themeMode: null,
    variables: null,
})

onBeforeMount(() => {
    page.drag = {
        vertical: {dir: 'vertical', move: true, removeOnSpill: false},
    }
    page.display = clone(props.modelValue)
    page.theme = page.display.theme
    page.themeMode = State.app.dark ? 'dark' : 'light'
    page.mode = page.theme[page.themeMode]
    for (let tag of ['colors', 'variables', 'formats']) {
        setProperties(tag)
    }
    window.addEventListener('keydown', keyboard, true)
})

onUnmounted(() => {
    window.removeEventListener('keydown', keyboard, true)
})

function emitDisplay() {
    let display = clone(props.modelValue)
    let mode = page.mode
    for (let tag of ['colors', 'variables', 'formats']) {
        mode[tag] = getProperties(tag)
    }
    page.theme[page.themeMode] = mode
    display.theme = page.theme
    emit('update:modelValue', display)
}

function getProperties(tag) {
    let properties = {}
    for (let property of page[tag] || []) {
        properties[property.name] = property.value
    }
    return properties
}

function setProperties(tag) {
    let properties = []
    let id = 1
    for (let [key, value] of Object.entries(page.mode[tag] || {})) {
        properties.push({id: id++, name: key, value: value})
    }
    page[tag] = properties
}

async function clicked({action, column, item, items, rec, row, tableName}) {
    let properties = page[tableName]
    if (action == 'add') {
        properties.push({name: '', value: ''})
    } else if (action == 'delete') {
        for (let item of items) {
            let index = properties.findIndex((p) => p.name == item.name)
            properties.splice(index, 1)
            emitDisplay()
        }
    } else if (column?.name == 'delete') {
        properties.splice(row, 1)
        emitDisplay()
    } else if (column?.name == 'swatch') {
        page.showColors[item.name] = true
    } else if (action == 'cell') {
    }
}

async function addProperty(tag) {
    let properties = page[tag].slice(0)
    let id = properties.length * 3 + 1
    properties.push({name: '', value: ''})
    page[tag] = properties
    await waitRender()
    let el = document.getElementById(`${tag}-${id}`)
    if (el) {
        el.focus()
    }
}

function sortProperties(tag) {
    let properties = page[tag].slice(0)
    properties.sort((a, b) => a.name.localeCompare(b.name))
    page[tag] = properties
}

async function editableEnterKey(event, props) {
    let {field, item, row} = props
    let value = event.target.innerText
    await blur(event, item, field, value, row)
    event.target.blur()
}

async function editableFocus(event, tag, props) {}

async function editableBlur(event, tag, props) {
    let {field, item, row} = props
    let value = event.target.innerText
    await blur(event, tag, item, field, value, row)
}

async function blur(event, tag, item, vfield, value, row = -1) {
    let properties = page[tag]
    if (properties.length <= row) {
        return
    }
    delete item._select_
    if (vfield.name == 'name') {
        properties[row].name = value
    } else if (vfield.name == 'value') {
        properties[row].value = value
    }
    if (row == properties.length - 1 && vfield.name == 'value') {
        //  Tab pressed in the last cell
        if (page.lastKeyPress == 'Tab') {
            addProperty(tag)
        }
        page.lastKeyPress = null
    }
    emitDisplay()

    /*
    if (page.lastKeyPress == 'Tab') {
        await waitRender()
        let el = document.getElementById(`${tag}-${row * 3 + 2}`)
        if (el) {
            el.focus()
        }
    } */
    page.lastKeyPress = null
}

function formatCell(item, vfield, value) {
    let fieldName = vfield.name
    vfield.attribute = fieldName
    if (fieldName == 'name' || fieldName == 'value') {
        vfield.editable = true
        vfield.css += ' editable'
    } else {
        vfield.editable = false
    }
    return (value || '').toString()
}

function colorPicked(tag, props, value) {
    let name = props.item.name
    page[tag][props.row] = {name, value}
}

function closeColorPicker() {
    page.showColors = {}
}

function themePicked(name) {
    page.themeMode = name
    page.mode = page.theme[name]
    for (let tag of ['colors', 'variables', 'formats']) {
        setProperties(tag)
    }
}

function keyboard(e) {
    page.showColors = {}
    if (e.key == 'Tab' && !e.shiftKey) {
        // Remember if tab pressed without shift key
        page.lastKeyPress = e.key
    }
}
async function dragged(event) {
    let {type, index, targetIndex} = event.detail
    if (type == 'drop') {
        let colors = clone(page.colors)
        colors.splice(targetIndex - 0, 0, ...colors.splice(index - 0, 1))
        page.colors = colors
    }
}
</script>

<template>
    <div class="display-theme-edit">
        <vu-input
            v-model="page.display.title"
            label="Display App Title"
            name="title"
            placeholder="Enter displayed App title"
            type="text"
            class="mb-2" />

        <v-expansion-panels>
            <v-expansion-panel>
                <v-expansion-panel-title>Theme Colors</v-expansion-panel-title>
                <v-expansion-panel-text>
                    <vu-input
                        v-model="page.themeMode"
                        label="Show Theme Mode"
                        name="showThemeMode0"
                        type="radio"
                        :items="{Dark: 'dark', Light: 'light'}"
                        @update:modelValue="themePicked($event)" />
                    <vu-input v-model="page.mode.logo" label="Logo" type="text" cols="6" />

                    <vu-table
                        class="theme-edit-table"
                        options="dynamic,toolbar"
                        name="colors"
                        title="Colors"
                        width="100%"
                        nodata="No Items"
                        :callbacks="{format: formatCell}"
                        :data="page.colors"
                        :fields="ColorFields"
                        :reload="false"
                        :drag="page.drag"
                        @click="clicked"
                        @drag="dragged">
                        <template v-slot:more="props">
                            <v-btn size="small" color="primary" class="mr-2 add" @click="addProperty('colors')">Add</v-btn>
                            <v-btn size="small" color="primary" class="mr-2 add" @click="sortProperties('colors')">Sort</v-btn>
                        </template>
                        <template v-slot:cell="props">
                            <div
                                v-if="props.field.editable"
                                spellcheck="false"
                                class="cell-value"
                                :contenteditable="props.field.editable"
                                :id="props.field.id"
                                :tabindex="props.field.id.replace(/.*([0-9]+)$/, '$1')"
                                @blur="editableBlur($event, 'colors', props)"
                                @focus="editableFocus($event, 'colors', props)"
                                @keydown.enter.prevent="editableEnterKey($event, props)">
                                {{ props.field.value || ' ' }}
                            </div>
                            <div v-else class="cell-value" contenteditable="false">{{ props.field.value }}</div>
                            <v-dialog
                                v-if="props.field.name == 'swatch' && props.item.name"
                                v-model="page.showColors[props.item.name]"
                                content-class="color-picker"
                                @afterLeave="closeColorPicker">
                                <v-color-picker
                                    v-model="props.item.value"
                                    @update:modelValue="colorPicked('colors', props, $event)"
                                    :hide-sliders="false"></v-color-picker>
                            </v-dialog>
                        </template>
                    </vu-table>
                </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel>
                <v-expansion-panel-title>Theme Variables</v-expansion-panel-title>
                <v-expansion-panel-text>
                    <vu-input
                        v-model="page.themeMode"
                        label="Show Theme Mode"
                        name="showThemeMode1"
                        type="radio"
                        :items="{Dark: 'dark', Light: 'light'}"
                        @update:modelValue="themePicked($event)" />
                    <vu-table
                        class="theme-edit-table"
                        options="dynamic,toolbar"
                        name="variables"
                        title="Variables"
                        width="100%"
                        nodata="No Items"
                        :callbacks="{format: formatCell}"
                        :data="page.variables"
                        :fields="Fields"
                        :reload="false"
                        @click="clicked">
                        <template v-slot:more="props">
                            <v-btn color="primary" class="mr-2 add" @click="addProperty('variables')">Add</v-btn>
                        </template>
                        <template v-slot:cell="props">
                            <div
                                v-if="props.field.editable"
                                spellcheck="false"
                                class="cell-value"
                                :contenteditable="props.field.editable"
                                :id="props.field.id"
                                :tabindex="props.field.id.replace(/.*([0-9]+)$/, '$1')"
                                @blur="editableBlur($event, 'variables', props)"
                                @focus="editableFocus($event, 'variables', props)"
                                @keydown.enter.prevent="editableEnterKey($event, props)">
                                {{ props.field.value || ' ' }}
                            </div>
                            <div v-else class="cell-value" contenteditable="false">{{ props.field.value }}</div>
                        </template>
                    </vu-table>
                </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel>
                <v-expansion-panel-title>Data Formats</v-expansion-panel-title>
                <v-expansion-panel-text>
                    <vu-input
                        v-model="page.themeMode"
                        label="Show Theme Mode"
                        name="showThemeMode2"
                        type="radio"
                        :items="{Dark: 'dark', Light: 'light'}"
                        @update:modelValue="themePicked($event)" />
                    <vu-table
                        class="theme-edit-table"
                        options="dynamic,toolbar"
                        name="formats"
                        title="Data Formats"
                        width="100%"
                        nodata="No Items"
                        :callbacks="{format: formatCell}"
                        :data="page.formats"
                        :fields="Fields"
                        @click="clicked">
                        <template v-slot:more="props">
                            <v-btn color="primary" class="mr-2 add" @click="addProperty('formats')">Add</v-btn>
                        </template>
                        <template v-slot:cell="props">
                            <div
                                v-if="props.field.editable"
                                spellcheck="false"
                                class="cell-value"
                                :contenteditable="props.field.editable"
                                :id="props.field.id"
                                :tabindex="props.field.id.replace(/.*([0-9]+)$/, '$1')"
                                @blur="editableBlur($event, 'formats', props)"
                                @focus="editableFocus($event, 'formats', props)"
                                @keydown.enter.prevent="editableEnterKey($event, props)">
                                {{ props.field.value || ' ' }}
                            </div>
                            <div v-else class="cell-value" contenteditable="false">{{ props.field.value }}</div>
                        </template>
                    </vu-table>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<style lang="scss" scoped>
.flex-row {
    display: flex;
    flex-wrap: wrap;
    column-gap: 12px;
    width: 100%;
}
.display-theme-edit {
    display: flex;
    flex-wrap: wrap;
    column-gap: 12px;
    width: 100%;
    margin-bottom: 16px;
    .v-expansion-panel {
        background-color: rgb(var(--v-theme-surface-lighten-1));
        border: solid 1px rgb(var(--v-theme-border));
        border-bottom: none;
        .v-expansion-panel-title {
            padding: 10px !important;
        }
        .v-expansion-panel-text__wrapper {
            padding: 10px !important;
        }
        .v-expansion-panel-title--active {
            border: solid 1px rgb(var(--v-theme-surface-lighten-1));
            background-color: rgb(var(--v-theme-surface-lighten-1));
            background: rgb(var(--v-theme-surface-lighten-2));
            font-size: 1.2rem;
        }
    }
}
.theme-edit-table {
    width: 100%;
    h2 {
        font-size: 1.4rem;
        font-weight: normal;
    }
    .v-alert {
        width: 100%;
    }
    .v-expansion-panel {
        .v-expansion-panel-title {
            padding: 10px !important;
        }
        .v-expansion-panel-text__wrapper {
            padding: 10px !important;
        }
    }
    label {
        color: rgb(var(--v-theme-text));
        font-weight: normal;
        font-size: 1.2rem;
    }
    td.editable {
        padding: 4px 7px 4px 7px;
        background: rgba(130, 178, 255, 0.2) !important;
        margin: 2px;
        cursor: text;
        &:focus {
            outline: 2px solid rgba(0, 0, 255, 0.75) !important;
            background: white !important;
            width: calc(100% - 4px);
        }
        .cell-value:focus,
        .cell-value:focus-visible {
            outline: 2px solid rgba(0, 0, 255, 0.75) !important;
        }
    }
    .table-cell {
        padding: 4px !important;
        .cell-attribute {
            padding: 3px;
            background: rgba(50, 50, 50, 0.1);
            color: black;
        }
    }
    .table-cell.table-col-name {
        .cell-value {
            display: inline-block;
            width: 100%;
            margin: 1px;
            padding: 3px;
        }
    }
    .table-cell.table-col-value {
        .cell-value {
            display: inline-block;
            width: calc(100% - 20px);
            margin: 1px;
            padding: 3px;
            padding-right: 0;
        }
    }
    .color-icon {
        margin-top: 2px;
        float: right;
        width: 16px;
    }
    .add {
        font-size: 12px;
    }
    ::v-deep(.table-title) {
        font-size: 1.4rem !important;
        _padding-top: 8px;
    }
}
.headline {
    background: rgb(var(--v-theme-primary));
    color: white;
    font-weight: bold;
    padding: 16px;
}
</style>

<style lang="scss">
.css-color-picker {
    width: fit-content !important;
}
.display-theme-edit {
    @media (max-width: 640px) {
        .v-expansion-panel {
            .v-expansion-panel-title {
                padding: 10px !important;
            }
            .v-expansion-panel-text__wrapper {
                padding: 10px !important;
            }
        }
    }
}
</style>